import * as React from 'react';
import { Field, FieldValue, FieldValues, Schema } from './schema';
import SectionCard from './SectionCard';
import { DifferingKeyDetail } from './fields/FieldWrapper';
import { GatingContext } from './utils/gating';
import { ControlOverrides } from './fields/controls';
import styles from './styles.module.css';

export interface SchemaSectionsProps {
  schema: Schema;
  values: FieldValues;
  initialValues: FieldValues;
  errors: Record<string, string>;
  gating: GatingContext;
  collapsed: Record<string, boolean>;
  onToggleCollapsed: (sectionId: string) => void;
  onChange: (id: string, value: FieldValue) => void;
  /** Bulk mode only: which keys differ across the selection. */
  differingDetail?: Map<string, DifferingKeyDetail[]>;
  /** Where each value comes from, for the forms that have layers beneath them. */
  inheritedDetail?: Map<string, DifferingKeyDetail[]>;
  hideDeviceModelLevel?: boolean;
  dimInherited?: boolean;
  onToggleStored?: (field: Field) => void;
  readOnly?: boolean;
  /** Fields the form holds at their current value; see `SectionCard`. */
  disabledFields?: string[];
  /** Controls the definition cannot describe; see `fields/controls.ts`. */
  controls?: ControlOverrides;
  /** Off for a form with no layer beneath it; see `fields/FieldWrapper`. Default on. */
  showFlags?: boolean;
  /** The field counts, changed counts and collapse. Off for a carer-facing form. */
  showChrome?: boolean;
  /** Rendered beside a named field, in the same row. */
  beside?: Record<string, React.ReactNode>;
}

const SchemaSections: React.FC<SchemaSectionsProps> = ({ schema, collapsed, onToggleCollapsed, ...forwarded }) => {
  const card = (sectionId: string) => {
    const section = schema.sections[sectionId];
    if (!section) return null;
    return (
      <SectionCard
        sectionId={sectionId}
        section={section}
        deviceType={schema.meta.device_type}
        collapsed={collapsed[sectionId]}
        onToggleCollapsed={() => onToggleCollapsed(sectionId)}
        {...forwarded}
      />
    );
  };

  return (
    <>
      {schema.layout.map((row, idx) => {
        if ('section' in row) return <React.Fragment key={`s-${idx}`}>{card(row.section)}</React.Fragment>;
        if ('split' in row) {
          return (
            <div key={`r-${idx}`} className={styles.splitRow}>
              <div className={styles.split}>
                {row.split.map((id) => (
                  // The empty div keeps the surviving section in its own column when its
                  // neighbour was pruned away, rather than letting it span both.
                  <div key={id}>{card(id)}</div>
                ))}
              </div>
            </div>
          );
        }
        return null;
      })}
    </>
  );
};

export default SchemaSections;
